import { AlertCircle, ArrowUpRight, Loader2, X } from "lucide-react";
import { memo, type FC, type ReactNode } from "react";
import { toast } from "sonner";

import { FileTypeIcon } from "@/components/common/FileTypeIcon";
import { cn } from "@/lib/utils";

import type { AgentScope } from "../../types";
import { AgentAttachmentsAPI } from "../agentAttachmentsApi";
import { useAttachmentScope } from "../AttachmentScopeContext";
import type { AttachmentFilePart, ComposerAttachment } from "../types";
import {
  attachmentIdOf,
  attachmentTypeLabel,
  fileMetaLine,
  fileMetaOf,
  type AttachmentFileMeta
} from "../utils";

/** Open the file in a new tab (fetched with auth, shown from a blob URL). */
const openAttachment = async (scope: AgentScope, id: string) => {
  const win = window.open("", "_blank");
  try {
    const blob = await AgentAttachmentsAPI.content(scope, id);
    const url = URL.createObjectURL(blob);
    if (win) win.location.href = url;
    else window.open(url, "_blank", "noopener");
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch {
    win?.close();
    toast.error("Couldn't open that file");
  }
};

const CARD =
  "relative inline-flex min-w-0 max-w-65 items-center gap-2.5 overflow-hidden rounded-xl border border-line-2 bg-card p-1.5 pr-3 text-left shadow-xs transition-[background-color,border-color,box-shadow]";

const OPENABLE =
  "cursor-pointer hover:border-border hover:bg-fill-1 hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40";

const IconWell: FC<{ children: ReactNode }> = ({ children }) => (
  <span className="flex size-8.5 shrink-0 items-center justify-center rounded-lg bg-fill-1">
    {children}
  </span>
);

const CardText: FC<{
  filename: string;
  meta: ReactNode;
  failed?: boolean;
}> = ({ filename, meta, failed = false }) => (
  <span className="flex min-w-0 flex-1 flex-col gap-0.5">
    <span className="truncate text-sm font-medium leading-4 text-foreground">
      {filename}
    </span>
    <span
      className={cn(
        "flex min-w-0 items-center gap-1 text-xs leading-3.5",
        failed ? "text-destructive" : "text-muted-foreground"
      )}
    >
      <span className="truncate">{meta}</span>
    </span>
  </span>
);

/** A file attached to a sent message. */
export const AttachmentChip: FC<{
  part: AttachmentFilePart;
  className?: string;
}> = memo(({ part, className }) => {
  const scope = useAttachmentScope();
  const id = attachmentIdOf(part.url);
  const filename = part.filename || "Attachment";
  const canOpen = Boolean(scope && id);
  const open = () => scope && id && void openAttachment(scope, id);

  const body = (
    <>
      <IconWell>
        <FileTypeIcon name={filename} mimeType={part.mediaType} size={22} />
      </IconWell>
      <CardText
        filename={filename}
        meta={fileMetaLine(
          attachmentTypeLabel(filename, part.mediaType),
          fileMetaOf(part)
        )}
      />
    </>
  );
  return canOpen ? (
    <button
      type="button"
      title={filename}
      className={cn(CARD, OPENABLE, "group/att", className)}
      onClick={open}
      data-testid="attachment-chip"
    >
      {body}
      <ArrowUpRight
        aria-hidden
        className="size-3.5 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover/att:opacity-100 group-focus-visible/att:opacity-100"
      />
    </button>
  ) : (
    <span
      title={filename}
      className={cn(CARD, className)}
      data-testid="attachment-chip"
    >
      {body}
    </span>
  );
});
AttachmentChip.displayName = "AttachmentChip";

/** A sent message's files, in a wrapping row. */
export const AttachmentList: FC<{
  parts: readonly AttachmentFilePart[];
  className?: string;
}> = ({ parts, className }) =>
  parts.length ? (
    <div
      className={cn("flex flex-wrap gap-2", className)}
      data-testid="message-attachments"
    >
      {parts.map((p, i) => (
        <AttachmentChip key={`${p.url}-${i}`} part={p} />
      ))}
    </div>
  ) : null;

const composerMeta = (
  item: ComposerAttachment,
  meta: AttachmentFileMeta
): ReactNode => {
  if (item.status === "uploading")
    return (
      <span className="inline-flex items-center gap-1">
        <Loader2 className="size-2.5 animate-spin" />
        Uploading {item.progress}%
      </span>
    );
  if (item.status === "failed") return "Upload failed";
  return fileMetaLine(
    attachmentTypeLabel(item.filename, item.contentType),
    meta
  );
};

/** A file in the composer: upload progress, failure, remove. */
export const ComposerAttachmentChip: FC<{
  item: ComposerAttachment;
  onRemove: () => void;
}> = ({ item, onRemove }) => {
  const failed = item.status === "failed";
  const uploading = item.status === "uploading";
  return (
    <span
      className="group/att relative inline-flex shrink-0"
      data-testid="composer-attachment"
      data-status={item.status}
      title={failed ? item.error : item.filename}
    >
      <span className={cn(CARD, failed && "border-destructive/50")}>
        <IconWell>
          {failed ? (
            <AlertCircle className="size-4 text-destructive" />
          ) : (
            <FileTypeIcon
              name={item.filename}
              mimeType={item.contentType}
              size={22}
            />
          )}
        </IconWell>
        <CardText
          filename={item.filename}
          failed={failed}
          meta={composerMeta(item, {
            sizeBytes: item.size,
            pageCount: item.attachment?.page_count ?? undefined
          })}
        />
        {uploading ? (
          <span className="absolute inset-x-0 bottom-0 h-0.5 bg-muted">
            <span
              className="block h-full bg-primary transition-[width]"
              style={{ width: `${item.progress}%` }}
            />
          </span>
        ) : null}
      </span>
      <button
        type="button"
        aria-label={`Remove ${item.filename}`}
        className={cn(
          "absolute -right-1 -top-1 flex size-3.5 items-center justify-center rounded-full ring-cutout ring-background bg-foreground text-background transition-opacity focus-visible:opacity-100 focus-visible:outline-none group-hover/att:opacity-100 group-focus-within/att:opacity-100 [@media(hover:none)]:opacity-100",
          failed ? "opacity-100" : "opacity-0"
        )}
        onClick={onRemove}
        data-testid="composer-attachment-remove"
      >
        <X className="size-2.5" strokeWidth={3} />
      </button>
    </span>
  );
};
